import { useMemo, useCallback, useState } from 'react';
import { Modal, ModalBody, ModalContent } from '@heroui/react';
import type { IStudentScormPackage } from '@/entities/Scorm';
import { SectionLoader } from '@/shared/ui/Loader';
import { useModalAutoFocus } from '@/shared/lib/hooks';
import { ScormCard } from '../ScormCard';

type ScormModalProps = {
  isOpen: boolean;
  onClose: () => void;
  scorm: IStudentScormPackage;
  scormUrl: string | null;
};

export const ScormModal = ({ isOpen, onClose, scorm, scormUrl }: ScormModalProps) => {
  useModalAutoFocus(isOpen);

  const [loadedSrc, setLoadedSrc] = useState<string | null>(null);
  const iframeSrc = useMemo(() => {
    if (!scormUrl) return '';
    return `${import.meta.env.VITE_API_URL_BASE}${scormUrl}`;
  }, [scormUrl]);
  const isIframeLoading = Boolean(iframeSrc && iframeSrc !== loadedSrc);

  const handleExit = useCallback(() => {
    setLoadedSrc(null);
    onClose();
  }, [onClose]);

  const handleIframeLoad = useCallback(() => {
    setLoadedSrc(iframeSrc);
  }, [iframeSrc]);

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={(open) => { if (!open) handleExit(); }}
      size="full"
      isDismissable={false}
      hideCloseButton
      classNames={{
        base: 'm-0 rounded-none h-screen max-h-screen',
        body: 'p-0 overflow-hidden',
        wrapper: 'overflow-hidden',
      }}
    >
      <ModalContent className="flex flex-col h-screen overflow-hidden">
        <div className="shrink-0 p-4">
          <ScormCard scorm={scorm} mode="player" onAction={handleExit} />
        </div>

        {isOpen && (
          <ModalBody className="flex-1 min-h-0 px-4 pb-4 !pt-0 relative">
            {iframeSrc ? (
              <>
                {isIframeLoading && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center bg-white">
                    <SectionLoader className="min-h-0" />
                  </div>
                )}
                <iframe
                  title="SCORM Content"
                  src={iframeSrc}
                  className="w-full h-full border-none rounded-lg"
                  onLoad={handleIframeLoad}
                />
              </>
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <p className="text-grey-secondary font-medium">
                  No launch URL found
                </p>
              </div>
            )}
          </ModalBody>
        )}
      </ModalContent>
    </Modal>
  );
};
